import { useCallback, useMemo } from "react";
import moment from "moment";
import { Modal, Table } from "antd";
import { useSelector } from "react-redux";
import { getAuthToken } from "Helper/AuthTokenHelper";
import { formatDateToDDMMYYYY } from "utils/CommonFunctions";

const medicineDetailsColumns = [
  {
    title: "Sr. No.",
    key: "sno",
    render: (text, data, index) => index + 1,
  },
  {
    title: "From Date",
    dataIndex: "from_date",
    key: "from_date",
    render: (e) => {
      return e ? formatDateToDDMMYYYY(e) : null;
    },
  },
  {
    title: "To Date",
    dataIndex: "to_date",
    key: "to_date",
    render: (e) => {
      return e ? formatDateToDDMMYYYY(e) : null;
    },
  },
  {
    title: "Medicine",
    dataIndex: "medicine",
    key: "medicine",
  },
  {
    title: "Dose",
    dataIndex: "dose",
    key: "dose",
  },
];

const labDetailColumns = [
  {
    title: "Sr. No.",
    key: "sno",
    render: (text, data, index) => index + 1,
  },
  {
    title: "Date",
    dataIndex: "date",
    key: "date",
    render: (e) => {
      return e ? formatDateToDDMMYYYY(e) : null;
    },
  },
  {
    title: "Type",
    dataIndex: "type",
    key: "type",
  },
  {
    title: "Progesterone",
    dataIndex: "progesterone",
    key: "progesterone",
  },
  {
    title: "FSH",
    dataIndex: "fsh",
    key: "fsh",
  },
  {
    title: "LH",
    dataIndex: "lh",
    key: "lh",
  },
  {
    title: "E2",
    dataIndex: "e2",
    key: "e2",
  },
  {
    title: "AMH",
    dataIndex: "amh",
    key: "amh",
  },
  {
    title: "AFC",
    dataIndex: "afc",
    key: "afc",
  },
];

const IvfFlowSheetModal = ({ isOpen, onClose }) => {
  const userData = getAuthToken();

  const theme = localStorage.getItem("default-theme");

  const { activePatientJourneyModuleDetails } = useSelector(
    ({ dashboard }) => dashboard
  );

  const marriedYearCount = useCallback((married_since) => {
    if (married_since !== "" || married_since !== null) {
      const currentDate = moment();
      const dob = moment(married_since);
      const years = currentDate.diff(dob, "years");
      return `${years}Y`;
    }
  }, []);

  const medicineDetails = useMemo(() => {
    return (
      activePatientJourneyModuleDetails?.medicine_detail?.map((item) => {
        return {
          ...item,
          id: Math.random().toString().substring(2, 9),
          step: item?.step_down ? "step_down" : item?.step_up ? "step_up" : "",
          isDelete: userData?.other === false ? true : false,
        };
      }) || []
    );
  }, [activePatientJourneyModuleDetails?.medicine_detail, userData?.other]);

  const labDetails = useMemo(() => {
    return (
      activePatientJourneyModuleDetails?.lab_detail?.map((item) => {
        return {
          ...item,
          id: Math.random().toString().substring(2, 9),
          isDelete: userData?.other === false ? true : false,
        };
      }) || []
    );
  }, [activePatientJourneyModuleDetails?.lab_detail, userData?.other]);

  return (
    <>
      <Modal
        centered
        open={isOpen}
        title="IVF Flow Sheet Details"
        className={`patient-registration-modal theme-${theme}`}
        maskClosable={false}
        onCancel={() => onClose()}
        width={860}
      >
        <div className="modal-details">
          <div className="modal-content-details">
            <div className="details">
              <div className="details-table">
                <div>Patient ID</div>
                <div>{activePatientJourneyModuleDetails?.patient_id}</div>
                <div>Patient Name</div>
                <div>
                  {activePatientJourneyModuleDetails?.patient_full_name}
                </div>
                <div>Partner Name</div>
                <div>
                  {activePatientJourneyModuleDetails?.partner_full_name}
                </div>
                <div>Married Since</div>
                <div>
                  {marriedYearCount(
                    activePatientJourneyModuleDetails?.anniversary
                  )}
                </div>
              </div>
            </div>
            <div className="details">
              <div className="table-title">Patient Details</div>
              <div className="details-table">
                <div>IVF ID</div>
                <div>{activePatientJourneyModuleDetails?.ivf_id}</div>
                <div>Protocol</div>
                <div>{activePatientJourneyModuleDetails?.protocol}</div>
                <div>Protocol Other</div>
                <div>{activePatientJourneyModuleDetails?.protocol_other}</div>
                <div>Factors</div>
                <div>{activePatientJourneyModuleDetails?.factors}</div>
                <div>Last Menstrual Period</div>
                <div>
                  {formatDateToDDMMYYYY(
                    activePatientJourneyModuleDetails?.last_menstrual_period
                  )}
                </div>
                <div>Planned Cycle</div>
                <div>{activePatientJourneyModuleDetails?.planned_cycle}</div>
              </div>
            </div>

            <div className="details">
              <div className="table-title">UltraSound Details</div>
              <div className="details-table">
                <div>Date</div>
                <div>
                  {activePatientJourneyModuleDetails?.ultrasoundDate
                    ? formatDateToDDMMYYYY(
                        activePatientJourneyModuleDetails?.ultrasoundDate
                      )
                    : ""}
                </div>
                <div>RT Side</div>
                <div>{activePatientJourneyModuleDetails?.rt_side}</div>
                <div>RT Number</div>
                <div>{activePatientJourneyModuleDetails?.rt_number}</div>
                <div>LT Side</div>
                <div>{activePatientJourneyModuleDetails?.lt_side}</div>
                <div>LT Number</div>
                <div>{activePatientJourneyModuleDetails?.lt_number}</div>
                <div>Endometrium</div>
                <div>{activePatientJourneyModuleDetails?.endometrium}</div>
              </div>
            </div>

            <div className="details">
              <div className="table-title">Pickup Details</div>
              <div className="details-table">
                <div>Trigger Date</div>
                <div>
                  {activePatientJourneyModuleDetails?.trigger_date
                    ? formatDateToDDMMYYYY(
                        activePatientJourneyModuleDetails?.trigger_date
                      )
                    : ""}
                </div>
                <div>Trigger Time</div>
                <div>
                  {activePatientJourneyModuleDetails?.trigger_time ?? ""}
                </div>
                <div>Pickup Date</div>
                <div>
                  {activePatientJourneyModuleDetails?.pick_up_date
                    ? formatDateToDDMMYYYY(
                        activePatientJourneyModuleDetails?.pick_up_date
                      )
                    : ""}
                </div>
                <div>Pickup Time</div>
                <div>
                  {activePatientJourneyModuleDetails?.pick_up_time ?? ""}
                </div>
                <div>Actual Pickup Time</div>
                <div>
                  {activePatientJourneyModuleDetails?.actual_pick_up_time ?? ""}
                </div>
                <div>Grade of OHSS</div>
                <div>{activePatientJourneyModuleDetails?.grade_of_ohss}</div>
                <div>Complication</div>
                <div>{activePatientJourneyModuleDetails?.complication}</div>
                <div>Complication Other</div>
                <div>
                  {activePatientJourneyModuleDetails?.complication_other}
                </div>
                <div>Doctor Name</div>
                <div>{activePatientJourneyModuleDetails?.doctor_name_id}</div>
              </div>
            </div>
            <div className="details">
              <div className="table-title">Semen Details</div>
              <div className="details-table">
                <div>Count</div>
                <div>{activePatientJourneyModuleDetails?.count}</div>
                <div>Motility</div>
                <div>{activePatientJourneyModuleDetails?.motility}</div>
                <div>Morphology (%)</div>
                <div>{activePatientJourneyModuleDetails?.morphology}</div>
                <div>DFI (%)</div>
                <div>{activePatientJourneyModuleDetails?.dfi}</div>
                <div>Vial ID</div>
                <div>{activePatientJourneyModuleDetails?.vial_id}</div>
                <div>Planned Sperm Collection</div>
                <div>
                  {activePatientJourneyModuleDetails?.planned_sperm_collection}
                </div>
                <div>Donor Profile / Affidavit</div>
                <div>{activePatientJourneyModuleDetails?.medicine}</div>
                <div>Done By</div>
                <div>{activePatientJourneyModuleDetails?.done_by_id}</div>
                <div>Sperm Present</div>
                <div>{activePatientJourneyModuleDetails?.sperm_presence}</div>
                <div>Additional Measures</div>
                <div>
                  {activePatientJourneyModuleDetails?.additional_measures}
                </div>
                <div>Diagnosis of PGT</div>
                <div>{activePatientJourneyModuleDetails?.diagnosis_of_pgt}</div>
              </div>
            </div>
            <div className="details">
              <div className="table-title">Notes</div>
              <div className="details-table">
                <div>Notes</div>
                <div>{activePatientJourneyModuleDetails?.notes}</div>
              </div>
            </div>
          </div>

          <div className="cmn_table_wrap table_wrapper mb-2">
            <div className="table-title">Medicine Details</div>

            <Table
              columns={medicineDetailsColumns}
              dataSource={medicineDetails}
              pagination={false}
            />
          </div>

          <div className="cmn_table_wrap table_wrapper mb-2">
            <div className="table-title">Lab Details</div>

            <Table
              columns={labDetailColumns}
              dataSource={labDetails}
              pagination={false}
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default IvfFlowSheetModal;
